import React, { useState } from "react";

import ptLocale from "date-fns/locale/pt-BR";
import esLocale from "date-fns/locale/es";
import enLocale from "date-fns/locale/en-US";

import {
  DocumentBox,
  DocumentBoxIdentifierContainer,
  DocumentMainInformations,
  DocumentName,
  DocumentSentAt,
  DocumentShowSignaturesContainer,
  DocumentShowSignaturesToggle,
  DocumentStatusContainer,
  DocumentStatusView,
} from "./styles";
import { IDocument } from "..";

import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import SignatureItem from "../SignatureItem/Index";
import {
  downloadDocusignDocument,
  getDocumentSignatures,
} from "../../../../../services/signature.service";
import Toastmessage from "../../../../Common/ToastMessage";
import { MdDownload } from "react-icons/md";
import { TiCancel } from "react-icons/ti"
import { CircularProgress } from "@material-ui/core";
import { getDateFormats, getDateTimeLocale } from "../../../../../utils/get-date-time-locale";

interface IDocumentItem {
  document: IDocument;
  canSeeDocumentSignatures: boolean;
}

export interface ISignature {
  memberId: string;
  memberName: string;
  memberEmail: string;
  minuteEnvelopeId: string;
  status: string;
  date: string;
}

const DOCUMENT_STATUS_ICONS = {
  sent: <HourglassEmptyIcon />,
  completed: (
    <CheckCircleIcon
      style={{ color: "#64B246", height: "24px", width: "24px" }}
    />
  ),
  declined: <CancelIcon style={{ color: "FF0000" }} />,
  voided: <TiCancel style={{ color: "#FF0000", height: "24px", width: "24px" }} />
};

const locales = {
  pt: ptLocale,
  es: esLocale,
  en: enLocale,
};

const DocumentItem = ({
  document,
  canSeeDocumentSignatures,
}: IDocumentItem) => {
  const { t, i18n } = useTranslation();
  const [isSignaturesExpanded, setIsSignaturesExpanded] = useState(false);
  const [signatures, setSignatures] = useState<ISignature[]>();
  const [isDownloadingEnvelope, setIsDownloadingEnvelope] = useState(false);

  const [toastMessage, setToastMessage] = useState({
    content: "",
    type: "error",
    open: false,
    autoHideDuration: 4000,
  });

  const handleChangeSignatureVisibility = async () => {
    setIsSignaturesExpanded(!isSignaturesExpanded);

    try {
      const response = await getDocumentSignatures(document.minuteEnvelopeId);

      setSignatures(response.data);
    } catch (err) {
      setToastMessage({
        content: t("forms.minutes.signatures.viewSignaturesError"),
        type: "error",
        open: true,
        autoHideDuration: 4000,
      });

      return;
    }
  };

  const handleDownloadDocusignEnvelope = async () => {
    setIsDownloadingEnvelope(true);
    try {
      await downloadDocusignDocument(document.docusignEnvelopeId);
    } catch (err) {
      setToastMessage({
        content: t("forms.minutes.signatures.downloadEnvelopeError"),
        type: "error",
        open: true,
        autoHideDuration: 4000,
      });
    } finally {
      setIsDownloadingEnvelope(false);
    }
  };

  const language = (i18n.language as keyof typeof locales) || "pt";

  const sentAtLocal = new Date(`${document.sentAt}Z`);

  const timePassed = formatDistanceToNow(sentAtLocal, {
    includeSeconds: true,
    addSuffix: true,
    locale: locales[language],
  });

  return (
    <>
      <Toastmessage
        message={toastMessage.content}
        type={toastMessage.type}
        open={toastMessage.open}
        autoHideDuration={toastMessage.autoHideDuration}
        close={() =>
          setToastMessage({
            open: false,
            content: "",
            type: "",
            autoHideDuration: 4000,
          })
        }
      />
      <DocumentBox key={document.docusignEnvelopeId}>
        <DocumentMainInformations isSignaturesExpanded={isSignaturesExpanded}>
          <DocumentBoxIdentifierContainer>
            <DocumentName>{document.name}</DocumentName>

            <DocumentSentAt>{timePassed}</DocumentSentAt>
          </DocumentBoxIdentifierContainer>
          <DocumentStatusContainer>
            <span>
              {
                DOCUMENT_STATUS_ICONS[
                  document.status as keyof typeof DOCUMENT_STATUS_ICONS
                ]
              }
            </span>

            <DocumentStatusView>
              {t(
                `forms.minutes.envelopes.status.${document.status.toLowerCase()}`
              )}
            </DocumentStatusView>

            {isDownloadingEnvelope ? (
              <CircularProgress
                size={"1rem"}
                style={{
                  marginLeft: "1rem",
                  marginTop: "4px",
                }}
              />
            ) : (
              <MdDownload
                style={{
                  minHeight: "24px",
                  minWidth: "24px",
                  cursor: "pointer",
                  marginLeft: "1rem",
                  marginTop: "2px",
                }}
                onClick={handleDownloadDocusignEnvelope}
              />
            )}
          </DocumentStatusContainer>

          <DocumentShowSignaturesContainer>
            {canSeeDocumentSignatures && (
              <DocumentShowSignaturesToggle
                onClick={handleChangeSignatureVisibility}
              >
                <span>{t("forms.minutes.signatures.viewSignatures")}</span>
                {isSignaturesExpanded ? (
                  <KeyboardArrowUpIcon style={{ marginTop: "-3px" }} />
                ) : (
                  <KeyboardArrowDownIcon style={{ marginTop: "-3px" }} />
                )}
              </DocumentShowSignaturesToggle>
            )}
          </DocumentShowSignaturesContainer>
        </DocumentMainInformations>
        {isSignaturesExpanded &&
          signatures &&
          signatures.map((signature, index) => {
            return (
              <SignatureItem key={index} signature={signature} index={index} />
            );
          })}
      </DocumentBox>
    </>
  );
};

export default DocumentItem;
